import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { 
    createUserWithEmailAndPassword, 
    updateProfile, 
    sendEmailVerification
} from 'firebase/auth'; 
import { 
    doc, 
    serverTimestamp, 
    collection, 
    writeBatch,
    query,
    where,
    getDocs,
    limit,
    increment
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import './Signup.css';

const Signup = () => {
    const navigate = useNavigate();
    const { code: urlCode } = useParams(); // For /join/:code route

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [username, setUsername] = useState('');
    const [phone, setPhone] = useState('');
    
    // Legal Agreement State
    const [agreed, setAgreed] = useState(false);

    // 🔥 REFERRAL STATE
    const [referralCode, setReferralCode] = useState('');
    const [referralStatus, setReferralStatus] = useState(null); // null, 'checking', 'valid', 'invalid'
    const [referralMessage, setReferralMessage] = useState('');
    const [validatedReferralCode, setValidatedReferralCode] = useState(null);
    const [referrerId, setReferrerId] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    // 🔥 Check URL for referral code on mount
    useEffect(() => {
        // Priority: URL param > query string > localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const queryCode = urlParams.get('ref') || urlParams.get('referral');
        const storedCode = localStorage.getItem('pendingReferralCode');
        
        const initialCode = urlCode || queryCode || storedCode;
        
        if (initialCode) {
            setReferralCode(initialCode.toUpperCase());
            validateReferralCode(initialCode);
        }
    }, [urlCode]);

    // 🔥 Validate referral code directly against Firestore
    const validateReferralCode = async (codeToValidate) => {
        const codeValue = (codeToValidate || referralCode).toUpperCase().trim();
        
        if (!codeValue || codeValue.length < 5) {
            setReferralStatus(null);
            setReferralMessage('');
            setValidatedReferralCode(null);
            setReferrerId(null);
            return;
        }

        setReferralStatus('checking');
        setReferralMessage('Checking code...');

        try {
            // Query Firestore to find user with this referral code
            const q = query(
                collection(db, 'users'),
                where('referralCode', '==', codeValue),
                limit(1)
            );
            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
                const referrerDoc = snapshot.docs[0];
                const referrerData = referrerDoc.data();
                const referrerName = referrerData.displayName || referrerData.username || referrerData.email?.split('@')[0] || 'A friend';
                
                setReferralStatus('valid');
                setReferralMessage(`Referred by ${referrerName}`);
                setValidatedReferralCode(codeValue);
                setReferrerId(referrerDoc.id);
                localStorage.setItem('pendingReferralCode', codeValue);
            } else {
                setReferralStatus('invalid');
                setReferralMessage('Invalid referral code');
                setValidatedReferralCode(null);
                setReferrerId(null);
                localStorage.removeItem('pendingReferralCode');
            }
        } catch (error) {
            console.error('Error validating code:', error);
            setReferralStatus('invalid');
            setReferralMessage('Error checking code');
            setValidatedReferralCode(null);
            setReferrerId(null);
        }
    };

    const handleReferralChange = (e) => {
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        setReferralCode(value);
        
        // Debounce validation
        if (value.length >= 5) {
            const timeoutId = setTimeout(() => validateReferralCode(value), 500);
            return () => clearTimeout(timeoutId);
        } else {
            setReferralStatus(null);
            setReferralMessage('');
            setValidatedReferralCode(null);
            setReferrerId(null);
        }
    };

    const clearReferralCode = () => {
        setReferralCode('');
        setReferralStatus(null);
        setReferralMessage('');
        setValidatedReferralCode(null);
        setReferrerId(null);
        localStorage.removeItem('pendingReferralCode');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!agreed) {
            setError('You must agree to the Terms of Service to continue.');
            return;
        }

        setLoading(true);

        try {
            // 1. Create user in Firebase Auth
            const cred = await createUserWithEmailAndPassword(auth, email, password);
            const displayNameValue = fullName || username || '';

            if (displayNameValue) {
                await updateProfile(cred.user, { displayName: displayNameValue });
            }

            // ⚡️ SECURITY LAYER: Send Verification Email
            await sendEmailVerification(cred.user);

            // 2. DATABASE BATCH WRITE (Profile + Notification + Referral)
            try {
                const batch = writeBatch(db);
                const uid = cred.user.uid;

                // 🔥 Generate unique referral code for new user
                const prefix = (email.split('@')[0] || 'USR').substring(0, 3).toUpperCase();
                const random = Math.random().toString(36).substring(2, 7).toUpperCase();
                const newUserReferralCode = `${prefix}${random}`;

                // A. Prepare User Profile
                const userRef = doc(db, 'users', uid);
                const userData = {
                    uid: uid,
                    email: cred.user.email,
                    displayName: displayNameValue || null,
                    fullName: fullName || null,
                    username: username || null,
                    phone: phone || null,
                    isPremium: false,
                    premiumPlan: null,
                    premiumUntil: null,
                    termsAgreed: true,
                    termsAgreedAt: serverTimestamp(),
                    createdAt: serverTimestamp(),
                    emailVerified: false,
                    // 🔥 REFERRAL FIELDS
                    referralCode: newUserReferralCode,
                    referralPoints: 0,
                    referralCount: 0,
                    referralSubscribers: 0,
                    referralRewardsEarned: 0,
                    milestonesAwarded: [],
                };

                // 🔥 If user was referred, add referral info
                if (validatedReferralCode && referrerId) {
                    userData.referredBy = referrerId;
                    userData.referredByCode = validatedReferralCode;
                    // 🎁 Give new user +1 bonus trial day
                    userData.trialBonusDays = 1;
                }

                batch.set(userRef, userData);

                // B. Prepare Welcome Notification
                const notifRef = doc(collection(db, 'users', uid, 'notifications'));
                batch.set(notifRef, {
                    title: "Action Required: Verify Email 🔒",
                    message: "Please check your inbox and verify your email to unlock full access.",
                    type: "alert",
                    read: false,
                    link: "#",
                    createdAt: serverTimestamp()
                });

                // 🔥 C. If referred, add referral bonus notification
                if (validatedReferralCode && referrerId) {
                    const bonusNotifRef = doc(collection(db, 'users', uid, 'notifications'));
                    batch.set(bonusNotifRef, {
                        title: "🎁 Referral Bonus Applied!",
                        message: "You've received +1 bonus trial day from your referral! Share your code to earn rewards too.",
                        type: "success",
                        read: false,
                        link: "/referral",
                        createdAt: serverTimestamp()
                    });

                    // 🔥 D. Update referrer's stats (+1 point, +1 count)
                    const referrerRef = doc(db, 'users', referrerId);
                    batch.update(referrerRef, {
                        referralPoints: increment(1),
                        referralCount: increment(1),
                    });

                    // 🔥 E. Create referral record
                    const referralDocRef = doc(collection(db, 'referrals'));
                    batch.set(referralDocRef, {
                        referrerId: referrerId,
                        referredUserId: uid,
                        referralCode: validatedReferralCode,
                        createdAt: serverTimestamp(),
                        referredUserSubscribed: false,
                        pointAwarded: true,
                        premiumAwarded: false,
                    });

                    // 🔥 F. Notify referrer
                    const referrerNotifRef = doc(collection(db, 'users', referrerId, 'notifications'));
                    batch.set(referrerNotifRef, {
                        title: "🎉 New Referral!",
                        message: `Someone just signed up using your referral code! You earned +1 point.`,
                        type: "success",
                        read: false,
                        link: "/referral",
                        createdAt: serverTimestamp()
                    });
                }

                // G. Commit batch
                await batch.commit();
                localStorage.removeItem('pendingReferralCode');

            } catch (dbErr) {
                console.error('Database Batch Error:', dbErr);
            }

            // 4. Show success screen
            setIsSubmitted(true);
        } catch (err) {
            console.error('Signup error', err);
            let msg = 'Failed to create account';
            if (err.code === 'auth/email-already-in-use') {
                msg = 'That email is already in use. Try signing in.';
            } else if (err.code === 'auth/weak-password') {
                msg = 'Password must be at least 6 characters.';
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="signup">
            {/* Background Atmosphere */}
            <div className="signup__bg-glow signup__bg-glow--one" />
            <div className="signup__bg-glow signup__bg-glow--two" />
            <div className="signup__bg-grid" />

            <div className="signup__container">
                {isSubmitted ? (
                    /* SUCCESS STATE */
                    <div className="signup__card signup__card--success">
                        <div className="signup__success-icon-wrap">
                            <Icon icon="solar:shield-check-bold" className="signup__success-icon" />
                        </div>

                        <h1 className="signup__title">Verify your email</h1>
                        <p className="signup__subtitle">
                            We've sent a secure verification link to <strong className="signup__email-highlight">{email}</strong>.
                        </p>
                        <p className="signup__subtitle" style={{marginTop: '10px', fontSize: '0.9rem'}}>
                            You <strong>must</strong> click the link in that email to activate your account.
                        </p>

                        {/* 🔥 Show referral bonus message */}
                        {validatedReferralCode && (
                            <div className="signup__referral-success">
                                <Icon icon="solar:gift-bold" className="signup__referral-success-icon" />
                                <span>Referral bonus applied! +1 trial day added.</span>
                            </div>
                        )}

                        <div className="signup__spam-box">
                            <Icon icon="solar:bell-bing-linear" className="signup__spam-icon" />
                            <div className="signup__spam-text">
                                <span className="signup__spam-title">Don't see it?</span>
                                <span>Check your <strong>Spam</strong> folder. </span>
                            </div>
                        </div>

                        <div className="signup__actions">
                            <button
                                type="button"
                                className="signup__btn signup__btn--primary"
                                onClick={() => navigate('/signin')}
                            >
                                <span>Proceed to Login</span>
                                <Icon icon="solar:login-linear" />
                            </button>
                        </div>
                    </div>
                ) : (
                    /* FORM STATE */
                    <div className="signup__card">
                        <div className="signup__header">
                            <div className="signup__logo-pill">
                                <Icon icon="solar:cpu-bolt-linear" />
                                <span>AiPredict ID</span>
                            </div>
                            <h1 className="signup__title">Create your account</h1>
                            <p className="signup__subtitle">
                                Join the platform automating football & crypto analysis.
                            </p>
                        </div>

                        <form className="signup__form" onSubmit={handleSubmit}>
                            {/* Email */}
                            <div className="signup__field-group">
                                <label className="signup__label">Email address</label>
                                <div className="signup__input-wrap">
                                    <Icon icon="solar:letter-linear" className="signup__input-icon" />
                                    <input
                                        type="email"
                                        className="signup__input"
                                        placeholder="name@example.com"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Password */}
                            <div className="signup__field-group">
                                <label className="signup__label">Password</label>
                                <div className="signup__input-wrap">
                                    <Icon icon="solar:lock-password-linear" className="signup__input-icon" />
                                    <input
                                        type="password"
                                        className="signup__input"
                                        placeholder="Create a strong password"
                                        required
                                        minLength={6}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Row for Name & User */}
                            <div className="signup__row">
                                <div className="signup__field-group">
                                    <label className="signup__label">Full Name</label>
                                    <div className="signup__input-wrap">
                                        <Icon icon="solar:user-circle-linear" className="signup__input-icon" />
                                        <input
                                            type="text"
                                            className="signup__input"
                                            placeholder="John Doe"
                                            required
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="signup__field-group">
                                    <label className="signup__label">Username</label>
                                    <div className="signup__input-wrap">
                                        <Icon icon="solar:user-id-linear" className="signup__input-icon" />
                                        <input
                                            type="text"
                                            className="signup__input"
                                            placeholder="@trader"
                                            required
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Phone (Optional) */}
                            <div className="signup__field-group">
                                <label className="signup__label">
                                    Phone number <span className="signup__label-opt">(Optional)</span>
                                </label>
                                <div className="signup__input-wrap">
                                    <Icon icon="solar:phone-linear" className="signup__input-icon" />
                                    <input
                                        type="tel"
                                        className="signup__input"
                                        placeholder="+1 234 567 890"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* 🔥 REFERRAL CODE INPUT */}
                            <div className="signup__field-group">
                                <label className="signup__label">
                                    <Icon icon="solar:gift-linear" className="signup__label-gift" />
                                    Referral Code <span className="signup__label-opt">(Optional)</span>
                                </label>
                                <div className={`signup__input-wrap signup__input-wrap--referral ${referralStatus ? `signup__input-wrap--${referralStatus}` : ''}`}>
                                    <Icon icon="solar:ticket-linear" className="signup__input-icon" />
                                    <input
                                        type="text"
                                        className="signup__input"
                                        placeholder="Enter referral code"
                                        value={referralCode}
                                        onChange={handleReferralChange}
                                        maxLength={10}
                                        style={{ textTransform: 'uppercase', letterSpacing: '1px' }}
                                    />
                                    {referralStatus === 'checking' && (
                                        <Icon icon="solar:refresh-circle-linear" className="signup__referral-status signup__referral-status--spin" />
                                    )}
                                    {referralStatus === 'valid' && (
                                        <Icon icon="solar:check-circle-bold" className="signup__referral-status signup__referral-status--valid" />
                                    )}
                                    {referralStatus === 'invalid' && (
                                        <button type="button" className="signup__referral-clear" onClick={clearReferralCode}>
                                            <Icon icon="solar:close-circle-bold" />
                                        </button>
                                    )}
                                </div>
                                {referralMessage && (
                                    <p className={`signup__referral-message signup__referral-message--${referralStatus}`}>
                                        {referralStatus === 'valid' && <Icon icon="solar:gift-bold" />}
                                        {referralMessage}
                                        {referralStatus === 'valid' && <span className="signup__referral-bonus"> +1 bonus trial day!</span>}
                                    </p>
                                )}
                            </div>

                            {/* LEGAL CHECKBOX START */}
                            <div className="signup__legal-wrap">
                                <label className="signup__legal-label">
                                    <input 
                                        type="checkbox" 
                                        className="signup__checkbox"
                                        checked={agreed}
                                        onChange={(e) => setAgreed(e.target.checked)}
                                    />
                                    <span className="signup__legal-text">
                                        I agree to the <Link to="/legal?tab=terms" target="_blank" className="signup__link">Terms of Service</Link>, and 
                                        <Link to="/legal?tab=risk" target="_blank" className="signup__link">Risk Disclosure</Link>.
                                    </span>
                                </label>
                            </div>
                            {/* LEGAL CHECKBOX END */}

                            {error && (
                                <div className="signup__error">
                                    <Icon icon="solar:danger-triangle-linear" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Button Disabled if !agreed */}
                            <button 
                                type="submit" 
                                className="signup__btn signup__btn--primary" 
                                disabled={loading || !agreed} 
                                style={{ opacity: !agreed ? 0.5 : 1, cursor: !agreed ? 'not-allowed' : 'pointer' }}
                            >
                                {loading ? (
                                    <span>Creating account...</span>
                                ) : (
                                    <>
                                        <span>Create Account</span>
                                        <Icon icon="solar:arrow-right-linear" />
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="signup__footer">
                            <p>Already have an account? <span onClick={() => navigate('/signin')}>Sign in</span></p>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Signup;